import React, { useState, useEffect } from 'react';
import Message from './Message';
import type { MessageType } from '../hooks/useConversations';
import '../styles/UserGroup.css';

interface UserGroupProps {
  userId: string;
  messages: MessageType[];
  currentLang: 'es' | 'en';
  onSendMessage?: (message: string) => void;
  expandAll?: boolean;
  assistantId: string;
}

const UserGroup: React.FC<UserGroupProps> = ({ userId, messages, currentLang, onSendMessage, expandAll = false, assistantId }) => {
  const [isOpen, setIsOpen] = useState(expandAll);
  useEffect(() => {
    setIsOpen(expandAll);
  }, [expandAll]);
  const [showContact, setShowContact] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<'success' | 'error' | null>(null);

  const t = (es: string, en: string) => (currentLang === 'es' ? es : en);

  // URL fija para enviar anuncio con su code
  const ANNOUNCE_URL = 'https://headspringchatbot.azurewebsites.net/api/enviar_anuncio?code=JKH6GMS7gyHQJZiYXONirlDB_C86jgIi4yHQsgF0OhZpAzFutEXZKw==';

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setSendStatus(null);
    // Extraer solo el teléfono antes del '|'
    const [phoneRaw] = userId.split('|').map(s => s.trim());
    const usuarioId = phoneRaw;
    try {
      const url = ANNOUNCE_URL;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          usuario: usuarioId,
          mensaje: contactMessage,
        }),
      });
      if (!res.ok) {
        throw new Error(t('Error de red', 'Network error'));
      }
      const data = await res.json();
      if (data.result) {
        setSendStatus('success');
        if (onSendMessage) {
          onSendMessage(contactMessage);
        }
        setContactMessage('');
        setTimeout(() => {
          setShowContact(false);
          setSendStatus(null);
        }, 2000);
      } else {
        setSendStatus('error');
      }
    } catch (error) {
      setSendStatus('error');
    } finally {
      setSending(false);
    }
  };

  // Construir displayName: email y teléfono enmascarado
  let displayName: string;
  if (userId.includes('|')) {
    const [phoneRaw, emailRaw] = userId.split('|').map(s => s.trim());
    const maskedPhone = phoneRaw.replace(/.(?=.{3})/g, '*');
    if (emailRaw) {
      displayName = `${emailRaw} | ${maskedPhone}`;
    } else {
      displayName = maskedPhone;
    }
  } else {
    displayName = `****${userId.slice(-5)}`;
  }
  return (
    <details className="user-group" open={isOpen} onToggle={e => setIsOpen((e.target as HTMLDetailsElement).open)}>
      <summary>{displayName}</summary>
      <div className="messages-container">
        {messages.map((msg, index) => (
          <Message
            key={index}
            role={msg.role}
            date={msg.date}
            content={msg.content}
            currentLang={currentLang}
            assistantId={assistantId}
          />
        ))}
      </div>
      <div className="contact-section">
        <button className="contact-btn" onClick={() => setShowContact(true)}>
          {t('Contactar', 'Contact')}
        </button>
        {showContact && (
          <form className="contact-form" onSubmit={handleContactSubmit}>
            <textarea
              value={contactMessage}
              onChange={e => setContactMessage(e.target.value)}
              maxLength={1000}
              placeholder={t('Escribe tu mensaje...', 'Write your message...')}
              required
            />
            <div className="form-actions">
              {sendStatus === 'success' && <p className="success-message">{t('Mensaje enviado con éxito', 'Message sent successfully')}</p>}
              {sendStatus === 'error' && <p className="error-message">{t('Error al enviar el mensaje', 'Error sending message')}</p>}
              <button type="button" onClick={() => setShowContact(false)}>{t('Cancelar', 'Cancel')}</button>
              <button type="submit" disabled={sending}>
                {sending ? t('Enviando...', 'Sending...') : t('Enviar', 'Send')}
              </button>
            </div>
          </form>
        )}
      </div>
    </details>
  );
};

export default UserGroup; 